import React from 'react';
import { Shield, AlertTriangle, Key, Lock, Users, ServerCrash } from 'lucide-react';
import MetricCard from '../../components/common/MetricCard';
import { useSecurityStats } from '../../hooks/useSecurityStats';
import Alert from '../../components/common/Alert';

// First, let's define our constants for key status thresholds
// Constants for key status thresholds
const KEY_STATUS = {
  VALID: 'valid',
  WARNING: 'warning',
  ERROR: 'error'
};

const KEY_ROTATION_THRESHOLDS = {
  WARNING: 30,  // Show warning when key is older than 30 days
  ERROR: 90    // Key is considered expired after 90 days
};

// Helper function to get key status based on age
const getKeyStatus = (keyAgeDays) => {
  if (keyAgeDays === null || keyAgeDays === undefined) return KEY_STATUS.ERROR;
  if (keyAgeDays >= KEY_ROTATION_THRESHOLDS.ERROR) return KEY_STATUS.ERROR;
  if (keyAgeDays >= KEY_ROTATION_THRESHOLDS.WARNING) return KEY_STATUS.WARNING;
  return KEY_STATUS.VALID;
};

// Helper function to get descriptive message
const getKeyStatusMessage = (keyAgeDays) => {
  if (keyAgeDays === null || keyAgeDays === undefined) return 'No key found';
  if (keyAgeDays >= KEY_ROTATION_THRESHOLDS.ERROR) return 'Key expired';
  if (keyAgeDays >= KEY_ROTATION_THRESHOLDS.WARNING) return 'Rotation recommended';
  if (keyAgeDays === 0) return 'Key created today';
  return 'Key valid';
};

const UserSecurityView = ({ security, isLoading }) => (
  <div className="space-y-6">
    <h2 className="text-lg font-medium text-gray-900">Your Security Status</h2>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <MetricCard
        title="Security Score"
        icon={<Shield className="h-5 w-5 text-blue-500" />}
        status={security?.security_score >= 80 ? 'success' : security?.security_score >= 60 ? 'warning' : 'error'}
        isLoading={isLoading}
        tooltip="Overall security rating based on your key status, email verification rate, and configuration settings"
      >
        <div className="text-3xl font-bold text-primary">
          {security?.security_score?.toFixed(1) || '0'}%
        </div>
        <p className="text-sm text-gray-500 mt-2">Overall security score</p>
      </MetricCard>

      <MetricCard
  title="Key Status"
  icon={<Key className="h-5 w-5 text-indigo-500" />}
  status={getKeyStatus(security?.key_age_days)}
  isLoading={isLoading}
  tooltip={`Keys should be rotated every ${KEY_ROTATION_THRESHOLDS.WARNING} days for optimal security. Keys older than ${KEY_ROTATION_THRESHOLDS.ERROR} days are considered expired.`}
>
  <div className="flex flex-col">
    <div className="text-3xl font-bold text-primary">
      {security?.key_age_days?.toFixed(0) || 0}
    </div>
    <p className="text-sm text-gray-500 mt-2">Days since key rotation</p>
    <p className="text-xs text-gray-400 mt-1">
      {getKeyStatusMessage(security?.key_age_days)}
    </p>
  </div>
</MetricCard>

      <MetricCard
        title="Email Security"
        icon={<Lock className="h-5 w-5 text-green-500" />}
        status={security?.verification_rate >= 80 ? 'success' : security?.verification_rate >= 60 ? 'warning' : 'error'}
        isLoading={isLoading}
        tooltip="Percentage of your emails that have been verified by recipients"
      >
        <div className="text-3xl font-bold text-primary">
          {security?.verification_rate?.toFixed(1) || '0'}%
        </div>
        <p className="text-sm text-gray-500 mt-2">Email verification rate</p>
      </MetricCard>

      <MetricCard
        title="Failed Events"
        icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
        status={security?.failed_emails > 10 ? 'error' : security?.failed_emails > 0 ? 'warning' : 'success'}
        isLoading={isLoading}
        tooltip="Number of failed email verification attempts or security-related failures"
      >
        <div className="text-3xl font-bold text-primary">
          {security?.failed_emails || 0}
        </div>
        <p className="text-sm text-gray-500 mt-2">Failed email attempts</p>
      </MetricCard>
    </div>
  </div>
);

const OrganizationSecurityView = ({ security, isLoading }) => {
  // Helper function to calculate percentages safely
  const calculatePercentage = (value, total) => {
    if (!value || !total || total === 0) return 0;
    return (value / total * 100).toFixed(1);
  };

  // Early return if no security data
  if (!security && !isLoading) {
    return (
      <Alert type="warning" title="No Organization Data">
        Organization security data is not available.
      </Alert>
    );
  }

  

  return (
    <div className="space-y-6 mt-8">
      <h2 className="text-lg font-medium text-gray-900">Organization Security Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Organization Score"
          icon={<Shield className="h-5 w-5 text-purple-500" />}
          status={security?.security_score >= 80 ? 'success' : security?.security_score >= 60 ? 'warning' : 'error'}
          isLoading={isLoading}
          tooltip="Overall security score for your entire organization"
        >
          <div className="text-3xl font-bold text-primary">
            {security?.security_score?.toFixed(1) || '0'}%
          </div>
          <p className="text-sm text-gray-500 mt-2">Organization security rating</p>
        </MetricCard>

        <MetricCard
          title="User Compliance"
          icon={<Users className="h-5 w-5 text-blue-500" />}
          status={calculatePercentage(security?.key_configured_users, security?.total_users) >= 80 ? 'success' : 
                  calculatePercentage(security?.key_configured_users, security?.total_users) >= 60 ? 'warning' : 'error'}
          isLoading={isLoading}
          tooltip="Number of users with properly configured security keys"
        >
          <div className="text-3xl font-bold text-primary">
            {security?.key_configured_users || 0}/{security?.total_users || 0}
          </div>
          <p className="text-sm text-gray-500 mt-2">Users with valid security keys</p>
        </MetricCard>

        <MetricCard
          title="Domain Security"
          icon={<ServerCrash className="h-5 w-5 text-indigo-500" />}
          status={calculatePercentage(security?.verified_domains, security?.total_domains) >= 80 ? 'success' : 
                  calculatePercentage(security?.verified_domains, security?.total_domains) >= 60 ? 'warning' : 'error'}
          isLoading={isLoading}
          tooltip="Number of verified domains out of total registered domains"
        >
          <div className="text-3xl font-bold text-primary">
            {security?.verified_domains || 0}/{security?.total_domains || 0}
          </div>
          <p className="text-sm text-gray-500 mt-2">Verified domains</p>
        </MetricCard>

        <MetricCard
          title="Email Security"
          icon={<Lock className="h-5 w-5 text-green-500" />}
          status={calculatePercentage(security?.verified_emails, security?.total_emails) >= 80 ? 'success' : 
                  calculatePercentage(security?.verified_emails, security?.total_emails) >= 60 ? 'warning' : 'error'}
          isLoading={isLoading}
          tooltip="Percentage of verified emails across your organization"
        >
          <div className="text-3xl font-bold text-primary">
            {calculatePercentage(security?.verified_emails, security?.total_emails)}%
          </div>
          <p className="text-sm text-gray-500 mt-2">Organization verification rate</p>
        </MetricCard>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Detailed Security Status</h3>
        </div>
        <div className="divide-y divide-gray-200">
          <div className="px-6 py-4 grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Active Users</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {security?.active_users || 0} / {security?.total_users || 0}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">SMTP Configured Users</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {security?.smtp_configured_users || 0}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Email Provider</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {security?.email_provider ? security.email_provider.charAt(0).toUpperCase() + security.email_provider.slice(1) : 'Not configured'}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Provider Status</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {security?.email_provider_configured ? 'Configured' : 'Not configured'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SecurityCenter() {
  const { 
    userSecurity,
    orgSecurity,
    isLoading,
    error,
    hasOrgAccess
  } = useSecurityStats();

  if (error) {
    return (
      <Alert type="error" title="Error loading security information">
        {error.message || 'Unable to load security metrics. Please try again later.'}
      </Alert>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Security Center</h1>
      </div>

      <UserSecurityView 
        security={userSecurity} 
        isLoading={isLoading} 
      />

      {hasOrgAccess && (
        <OrganizationSecurityView 
          security={orgSecurity}
          isLoading={isLoading}
        />
      )}
    </div>
  );
}